import React, { useState, useEffect } from 'react';
import { Plus, FileText, Activity, Zap, TrendingUp, ShieldAlert, BadgeInfo, Bot } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SaasLizChat from './SaasLizChat';
import SaasLizTrainingHub from './SaasLizTrainingHub';
import SaasMeusDocumentos from './SaasMeusDocumentos';
import AlterarSenhaModal from './AlterarSenhaModal';
import VisaoGeralPage from './pages/VisaoGeralPage';
import GestaoTenantsPage from './pages/GestaoTenantsPage';
import FaturamentoPage from './pages/FaturamentoPage';
import PlanosPage from './pages/PlanosPage';
import AuditoriaPage from './pages/AuditoriaPage';
import ConfiguracoesPage from './pages/ConfiguracoesPage';

// ─── Tipos ────────────────────────────────────────────────────────────────────



// ─── Componente ───────────────────────────────────────────────────────────────

export default function SaasDashboard() {
  const { usuario, logout } = useAuth();
  const [showLizChat, setShowLizChat] = useState(false);
  const [paginaAtiva, setPaginaAtiva] = useState<'visao-geral' | 'gestao-tenants' | 'faturamento' | 'planos' | 'auditoria' | 'configuracoes' | 'treinar-liz' | 'meus-documentos'>('visao-geral');
  const [showAlterarSenha, setShowAlterarSenha] = useState(false);

  // ── Drag state (clássico React) ─────────────────────────────────────────
  const [pos, setPos] = useState({ x: 400, y: 150 });
  const [size, setSize] = useState({ width: 420, height: 580 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const startDrag = (e: React.MouseEvent) => {
    setDragging(true);
    setOffset({ x: e.clientX - pos.x, y: e.clientY - pos.y });
    e.preventDefault();
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e: MouseEvent) => {
      setPos({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };
    const stop = () => setDragging(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
    };
  }, [dragging, offset]);

  // ── Resize state (4 quinas) ─────────────────────────────────────────────
  const startResize = (corner: 'tl' | 'tr' | 'bl' | 'br') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const mouseStartX = e.clientX;
    const mouseStartY = e.clientY;
    const startW = size.width;
    const startH = size.height;
    const startX = pos.x;
    const startY = pos.y;
    const MIN_W = 340;
    const MIN_H = 360;

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - mouseStartX;
      const dy = ev.clientY - mouseStartY;
      let newW = startW;
      let newH = startH;
      let newX = startX;
      let newY = startY;

      if (corner === 'br') { newW = startW + dx; newH = startH + dy; }
      else if (corner === 'bl') { newW = startW - dx; newH = startH + dy; newX = startX + dx; }
      else if (corner === 'tr') { newW = startW + dx; newH = startH - dy; newY = startY + dy; }
      else if (corner === 'tl') { newW = startW - dx; newH = startH - dy; newX = startX + dx; newY = startY + dy; }

      if (newW < MIN_W) {
        if (corner === 'bl' || corner === 'tl') newX = startX + (startW - MIN_W);
        newW = MIN_W;
      }
      if (newH < MIN_H) {
        if (corner === 'tl' || corner === 'tr') newY = startY + (startH - MIN_H);
        newH = MIN_H;
      }

      setSize({ width: newW, height: newH });
      setPos({ x: newX, y: newY });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-40 bg-slate-800/80 backdrop-blur-md border-b border-slate-700 shadow-lg">
        <div className="max-w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                🚀
              </div>
              <h1 className="text-xl font-bold text-white">Alencastro SaaS</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-400">{usuario?.email?.split('@')[0]}</span>
              <button
                onClick={() => setShowAlterarSenha(true)}
                className="px-4 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                title="Alterar minha senha"
              >
                🔐 <span>Alterar Senha</span>
              </button>
              <button
                onClick={logout}
                className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors text-sm font-medium"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Horizontal Navigation Tabs */}
      <div className="bg-slate-800/40 border-b border-slate-700 sticky top-[65px] z-30 backdrop-blur">
        <div className="max-w-full px-6">
          <div className="flex gap-2 overflow-x-auto py-0">
            {[
              { id: 'visao-geral', label: '📊 Visão Geral' },
              { id: 'gestao-tenants', label: '🏫 Gestão de Tenants' },
              { id: 'faturamento', label: '💵 Faturamento' },
              { id: 'planos', label: '📋 Planos' },
              { id: 'treinar-liz', label: '🎓 Treinar LIZ' },
              { id: 'meus-documentos', label: '📄 Meus Documentos' },
              { id: 'auditoria', label: '🛡️ Auditoria' },
              { id: 'configuracoes', label: '⚙️ Configurações' },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setPaginaAtiva(id as any)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                  paginaAtiva === id
                    ? 'text-blue-400 border-blue-500'
                    : 'text-slate-400 border-transparent hover:text-slate-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-full px-6 py-8">
        {/* Render different pages based on navigation */}
        {paginaAtiva === 'visao-geral' && <VisaoGeralPage />}
        {paginaAtiva === 'gestao-tenants' && <GestaoTenantsPage />}
        {paginaAtiva === 'faturamento' && <FaturamentoPage />}
        {paginaAtiva === 'planos' && <PlanosPage />}
        {paginaAtiva === 'treinar-liz' && <SaasLizTrainingHub />}
        {paginaAtiva === 'meus-documentos' && <SaasMeusDocumentos />}
        {paginaAtiva === 'auditoria' && <AuditoriaPage />}
        {paginaAtiva === 'configuracoes' && <ConfiguracoesPage />}
      </main>

      {/* Liz Chat - Floating Button */}
      <button
        onClick={() => setShowLizChat(!showLizChat)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center overflow-hidden p-0 z-40"
        title="Abrir chat com Liz"
      >
        <img src="/liz_avatar.png" alt="Liz" className="w-full h-full rounded-full object-cover" />
      </button>

      {/* Liz Chat Modal - Draggable + Resizable */}
      {showLizChat && (
        <div
          style={{
            position: 'fixed',
            left: pos.x,
            top: pos.y,
            width: size.width,
            height: size.height,
            zIndex: 9999,
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            borderRadius: '1.5rem',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* BARRA DE ARRASTAR - bem visível, cor pink */}
          <div
            onMouseDown={startDrag}
            style={{
              background: 'linear-gradient(90deg, #ff4081, #e91e63)',
              color: 'white',
              padding: '10px 16px',
              cursor: dragging ? 'grabbing' : 'grab',
              fontWeight: 700,
              fontSize: '13px',
              letterSpacing: '1px',
              textAlign: 'center',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <span>⬌ ARRASTE AQUI PARA MOVER</span>
            <button
              onClick={() => setShowLizChat(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
              title="Fechar"
            >
              ✕
            </button>
          </div>

          {/* Conteúdo do chat preenche o resto */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
            <SaasLizChat />
          </div>

          {/* Handles de redimensionamento nas 4 quinas */}
          <div
            onMouseDown={startResize('tl')}
            title="Redimensionar"
            style={{
              position: 'absolute', top: 0, left: 0, width: 18, height: 18,
              cursor: 'nwse-resize', zIndex: 10000,
              background: 'rgba(255,255,255,0.4)',
              borderRight: '2px solid #fff', borderBottom: '2px solid #fff',
              borderTopLeftRadius: '1.5rem',
            }}
          />
          <div
            onMouseDown={startResize('tr')}
            title="Redimensionar"
            style={{
              position: 'absolute', top: 0, right: 0, width: 18, height: 18,
              cursor: 'nesw-resize', zIndex: 10000,
              background: 'rgba(255,255,255,0.4)',
              borderLeft: '2px solid #fff', borderBottom: '2px solid #fff',
              borderTopRightRadius: '1.5rem',
            }}
          />
          <div
            onMouseDown={startResize('bl')}
            title="Redimensionar"
            style={{
              position: 'absolute', bottom: 0, left: 0, width: 18, height: 18,
              cursor: 'nesw-resize', zIndex: 10000,
              background: 'rgba(255,64,129,0.6)',
              borderRight: '2px solid #ff4081', borderTop: '2px solid #ff4081',
              borderBottomLeftRadius: '1.5rem',
            }}
          />
          <div
            onMouseDown={startResize('br')}
            title="Redimensionar"
            style={{
              position: 'absolute', bottom: 0, right: 0, width: 22, height: 22,
              cursor: 'nwse-resize', zIndex: 10000,
              background: 'rgba(255,64,129,0.8)',
              borderLeft: '2px solid #ff4081', borderTop: '2px solid #ff4081',
              borderBottomRightRadius: '1.5rem',
            }}
          />
        </div>
      )}

      {/* System Metrics Footer */}
      <footer className="bg-slate-800/40 border-t border-slate-700 mt-12 py-8">
        <div className="max-w-full px-6">
          <h3 className="text-white font-bold mb-6">⚙️ Métricas do Sistema</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-slate-400 text-xs uppercase font-bold mb-2">Tokens Globais</p>
              <p className="text-2xl font-bold text-blue-400">0</p>
              <p className="text-xs text-slate-500 mt-2">Sem dados ainda</p>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-4">
              <p className="text-slate-400 text-xs uppercase font-bold mb-2">Custo Estimado</p>
              <p className="text-2xl font-bold text-green-400">$0.00</p>
              <p className="text-xs text-slate-500 mt-2">Sem dados ainda</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-4">
              <p className="text-slate-400 text-xs uppercase font-bold mb-2">APIs Ativas</p>
              <p className="text-2xl font-bold text-purple-400">0 / 0</p>
              <p className="text-xs text-slate-500 mt-2">Sem dados ainda</p>
            </div>
            <div className="bg-gradient-to-br from-rose-500/20 to-rose-600/10 border border-rose-500/30 rounded-xl p-4">
              <p className="text-slate-400 text-xs uppercase font-bold mb-2">Saúde do Sistema</p>
              <p className="text-2xl font-bold text-rose-400">—</p>
              <p className="text-xs text-slate-500 mt-2">Sem dados ainda</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal: Alterar Senha */}
      {showAlterarSenha && <AlterarSenhaModal onClose={() => setShowAlterarSenha(false)} />}
    </div>
  );
}
